import React, { useState } from "react";
import { observer } from "mobx-react";
import SectionSources from "./SectionSources";

const SOURCE_BADGES = {
  job_posting: { label: "Job Posting", icon: "💼" },
  funding: { label: "Funding", icon: "💰" },
  timing: { label: "Timing", icon: "⏰" },
  career_history: { label: "Career", icon: "📋" },
  linkedin_post: { label: "LinkedIn", icon: "📝" },
  company_news: { label: "News", icon: "📰" },
  tech_stack: { label: "Tech Stack", icon: "🔧" },
  competitive: { label: "Competitive", icon: "⚔️" },
  review_site: { label: "Review", icon: "⭐" },
  deep_dive: { label: "Deep Dive", icon: "🔍" }
};

const URGENCY_STYLES = {
  high:   { dot: "#ef4444", bg: "#fef2f2", border: "#fecaca", color: "#991b1b", label: "High" },
  medium: { dot: "#f59e0b", bg: "#fffbeb", border: "#fde68a", color: "#92400e", label: "Medium" },
  low:    { dot: "#9ca3af", bg: "#f9fafb", border: "#e5e7eb", color: "#4b5563", label: "Low" }
};

// LEGACY field set — older signals in Mongo may have these instead of the new shape.
const LEGACY_PLAYBOOK_FIELDS = [
  { key: "why_it_matters",            label: "Why it matters",    icon: "🎯" },
  { key: "how_it_connects_to_seller", label: "How it connects",   icon: "🔗" },
  { key: "preempt_objection",         label: "Preempt objection", icon: "🛡️" },
  { key: "outcome_to_aim_for",        label: "Outcome",           icon: "✅" }
];

const SignalCard = ({ signal, defaultExpanded }) => {
  const [expanded, setExpanded] = useState(!!defaultExpanded);
  const [copied, setCopied] = useState(false);

  const urgencyKey = (signal.urgency || "").toLowerCase();
  const urgency = URGENCY_STYLES[urgencyKey] || URGENCY_STYLES.low;
  const source = signal.source
    ? (SOURCE_BADGES[signal.source] || { label: signal.source, icon: "📌" })
    : null;

  const headline = signal.signal || signal.trigger_insight || "";
  const trigger = signal.trigger || "";
  const hook = signal.opener_hook || signal.what_to_say || "";

  // NEW schema fields
  const painLogic = signal.pain_logic || "";
  const expectedObjection = signal.expected_objection || "";
  const counterPlay = signal.counter_play || "";
  const aimFor = signal.aim_for || "";

  // Detect new vs legacy schema. If we have pain_logic OR any new playbook
  // field, render the new layout. Otherwise fall back to the legacy 4-field
  // playbook so existing Mongo rows still display.
  const hasNewSchema = !!(painLogic || expectedObjection || counterPlay || aimFor);

  const legacyEntries = LEGACY_PLAYBOOK_FIELDS
    .map(f => ({ ...f, value: signal[f.key] }))
    .filter(e => e.value);

  const hasExpandableContent = trigger || hook || painLogic || expectedObjection
                              || counterPlay || aimFor || legacyEntries.length > 0;

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };

  return (
    <div className={`drSignalCardV2 drSignalCardV2--${urgencyKey || "low"}`}>
      <button
        type="button"
        className="drSignalCardV2__head"
        onClick={() => hasExpandableContent && setExpanded(!expanded)}
        disabled={!hasExpandableContent}
      >
        <span className="drSignalCardV2__strip" style={{ backgroundColor: urgency.dot }} />
        <div className="drSignalCardV2__headBody">
          <div className="drSignalCardV2__topRow">
            {urgency && (
              <span
                className="drSignalCardV2__urgencyPill"
                style={{ background: urgency.bg, color: urgency.color, borderColor: urgency.border }}
              >
                <span className="drSignalCardV2__urgencyDot" style={{ backgroundColor: urgency.dot }} />
                {urgency.label}
              </span>
            )}
            {source && (
              <span className="drSignalCardV2__sourcePill">
                <span className="drSignalCardV2__sourceIcon">{source.icon}</span>
                {source.label}
              </span>
            )}
            {hasExpandableContent && (
              <span className={`drSignalCardV2__chevron ${expanded ? "is-open" : ""}`}>
                <i className="material-icons-outlined">expand_more</i>
              </span>
            )}
          </div>

          {headline && <p className="drSignalCardV2__headline">{headline}</p>}
          {!expanded && painLogic && (
            <p className="drSignalCardV2__teaser">{painLogic}</p>
          )}
          {!expanded && !painLogic && signal.why_it_matters && (
            <p className="drSignalCardV2__teaser">{signal.why_it_matters}</p>
          )}
        </div>
      </button>

      {expanded && hasExpandableContent && (
        <div className="drSignalCardV2__body">
          {hasNewSchema ? (
            // ─── NEW LAYOUT: pain_logic narrative + 3-chip playbook ───
            <>
              {(trigger || hook) && (
                <div className="drSignalCardV2__evidenceBlock">
                  {trigger && (
                    <div className="drSignalCardV2__triggerBlock">
                      <span className="drSignalCardV2__triggerLabel">Trigger</span>
                      <p className="drSignalCardV2__triggerText">{trigger}</p>
                    </div>
                  )}
                </div>
              )}

              {painLogic && (
                <div className="drSignalCardV2__painLogic">
                  <span className="drSignalCardV2__sectionLabel">Pain logic</span>
                  <p className="drSignalCardV2__painLogicText">{painLogic}</p>
                </div>
              )}

              {hook && (
                <div className="drSignalCardV2__hookBlock">
                  <span className="drSignalCardV2__sectionLabel">💬 Suggested opener (paste-ready)</span>
                  <div className="drSignalCardV2__hookText">{hook}</div>
                  <button
                    type="button"
                    className={`drSignalCardV2__copyBtn ${copied ? "is-copied" : ""}`}
                    onClick={(e) => { e.stopPropagation(); copyToClipboard(hook); }}
                  >
                    <i className="material-icons-outlined">
                      {copied ? "check" : "content_copy"}
                    </i>
                    {copied ? "Copied" : "Copy opener"}
                  </button>
                </div>
              )}

              {(expectedObjection || counterPlay || aimFor) && (
                <div className="drSignalCardV2__playStrip">
                  {expectedObjection && (
                    <div className="drSignalCardV2__playChip drSignalCardV2__playChip--objection">
                      <span className="drSignalCardV2__playChipIcon">🛡️</span>
                      <div className="drSignalCardV2__playChipBody">
                        <span className="drSignalCardV2__playChipLabel">Expected objection</span>
                        <span className="drSignalCardV2__playChipValue">{expectedObjection}</span>
                      </div>
                    </div>
                  )}
                  {counterPlay && (
                    <div className="drSignalCardV2__playChip drSignalCardV2__playChip--counter">
                      <span className="drSignalCardV2__playChipIcon">⚡</span>
                      <div className="drSignalCardV2__playChipBody">
                        <span className="drSignalCardV2__playChipLabel">Your counter</span>
                        <span className="drSignalCardV2__playChipValue">{counterPlay}</span>
                      </div>
                    </div>
                  )}
                  {aimFor && (
                    <div className="drSignalCardV2__playChip drSignalCardV2__playChip--aim">
                      <span className="drSignalCardV2__playChipIcon">🎯</span>
                      <div className="drSignalCardV2__playChipBody">
                        <span className="drSignalCardV2__playChipLabel">Aim for</span>
                        <span className="drSignalCardV2__playChipValue">{aimFor}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            // ─── LEGACY LAYOUT: 2-col Evidence | Playbook (back-compat for old rows) ───
            <div className="drSignalCardV2__cols">
              <div className="drSignalCardV2__col">
                <div className="drSignalCardV2__colLabel">Evidence</div>
                {trigger && (
                  <div className="drSignalCardV2__triggerBlock">
                    <span className="drSignalCardV2__triggerLabel">Trigger</span>
                    <p className="drSignalCardV2__triggerText">{trigger}</p>
                  </div>
                )}
                {hook && (
                  <div className="drSignalCardV2__quote">
                    <span className="drSignalCardV2__quoteMark">"</span>
                    <p className="drSignalCardV2__quoteText">{hook}</p>
                  </div>
                )}
                {!trigger && !hook && (
                  <p className="drSignalCardV2__empty">No source quote captured.</p>
                )}
              </div>
              <div className="drSignalCardV2__col">
                <div className="drSignalCardV2__colLabel">Playbook</div>
                {legacyEntries.length === 0 && (
                  <p className="drSignalCardV2__empty">No playbook captured.</p>
                )}
                {legacyEntries.map(e => (
                  <div className="drSignalCardV2__playRow" key={e.key}>
                    <span className="drSignalCardV2__playIcon">{e.icon}</span>
                    <div className="drSignalCardV2__playText">
                      <span className="drSignalCardV2__playLabel">{e.label}</span>
                      <span className="drSignalCardV2__playValue">{e.value}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SellingSignals = observer(({ signals, serpLinks }) => {
  if (!signals || signals.length === 0) return null;

  // Sort by urgency when present, otherwise keep original order
  const sorted = [...signals].sort((a, b) => {
    const order = { high: 0, medium: 1, low: 2 };
    const aKey = (a.urgency || "").toLowerCase();
    const bKey = (b.urgency || "").toLowerCase();
    return (order[aKey] ?? 99) - (order[bKey] ?? 99);
  });

  return (
    <div className="drSection drSellingSignalsV2">
      <div className="drSectionHeader">
        <span className="drSectionIcon">🎯</span>
        <h3>Selling Signals</h3>
        <span className="drSectionCount">{signals.length}</span>
      </div>
      <div className="drSignalsListV2">
        {sorted.map((s, idx) => (
          <SignalCard key={idx} signal={s} defaultExpanded={false} />
        ))}
      </div>

      <SectionSources
        serpLinks={serpLinks}
        labels={[
          "COMPANY_JOB_POSTINGS",
          "COMPANY_LEADERSHIP_CHANGES",
          "COMPANY_FINANCIAL_DATA",
          "COMPANY_COMPETITIVE_SIGNALS"
        ]}
      />
    </div>
  );
});

export default SellingSignals;
